import {ActionList, ActionMenu, StateLabel} from '@primer/react'
import {clsx} from 'clsx'
import {STATE_LABEL_DATA, type PullRequestState as PullRequestStateType} from '../types'
import {useIsOverviewEnabled} from '../hooks/use-feature-state'
import {useAvailableStateChangeActions} from './state-change-actions/use-available-state-change-actions'
import {TriangleDownIcon} from '@primer/octicons-react'
import styles from './PullRequestStateLabel.module.css'
import {
  MarkReadyForReviewAction,
  MarkReadyForReviewFailureDialog,
  useMarkReadyForReviewActionMutation,
} from './state-change-actions/MarkReadyForReviewAction'
import {
  ConvertToDraftAction,
  ConvertToDraftFailureDialog,
  useConvertToDraftActionMutation,
} from './state-change-actions/ConvertToDraftAction'
import {CloseAction, CloseFailureDialog, useCloseActionMutation} from './state-change-actions/CloseAction'
import {ReopenAction, ReopenFailureDialog, useReopenActionMutation} from './state-change-actions/ReopenAction'

interface PullRequestStateLabelProps {
  className?: string
  state: PullRequestStateType
}

export function PullRequestStateLabel({className, state}: PullRequestStateLabelProps) {
  // Mutations live here rather than in the menu items because the menu overlay unmounts on select.
  const markReadyForReviewMutation = useMarkReadyForReviewActionMutation()
  const convertToDraftMutation = useConvertToDraftActionMutation()
  const closeMutation = useCloseActionMutation()
  const reopenMutation = useReopenActionMutation()

  const stateChangeActions = useAvailableStateChangeActions({
    markReadyForReviewMutation,
    convertToDraftMutation,
    closeMutation,
    reopenMutation,
  })

  const isMenu = useIsOverviewEnabled() && stateChangeActions.length > 0

  const stateLabel = STATE_LABEL_DATA[state]

  const label = (
    <StateLabel className={clsx('flex-self-start', styles.label, className)} status={stateLabel.status}>
      {stateLabel.description}
      {isMenu && <TriangleDownIcon className="ml-1 mr-n1" />}
    </StateLabel>
  )

  return (
    <>
      {isMenu ? (
        <ActionMenu>
          <ActionMenu.Anchor>
            <button aria-label={`Pull request state: ${stateLabel.description}`} className={styles.anchorWrapper}>
              {label}
            </button>
          </ActionMenu.Anchor>

          <ActionMenu.Overlay>
            <ActionList>
              {stateChangeActions.map(name => {
                switch (name) {
                  case 'mark-ready':
                    return <MarkReadyForReviewAction key={name} mutation={markReadyForReviewMutation} />
                  case 'convert-to-draft':
                    return <ConvertToDraftAction key={name} mutation={convertToDraftMutation} />
                  case 'close':
                    return <CloseAction key={name} mutation={closeMutation} />
                  case 'reopen':
                    return <ReopenAction key={name} mutation={reopenMutation} />
                }
              })}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      ) : (
        label
      )}

      <MarkReadyForReviewFailureDialog mutation={markReadyForReviewMutation} />
      <ConvertToDraftFailureDialog mutation={convertToDraftMutation} />
      <CloseFailureDialog mutation={closeMutation} />
      <ReopenFailureDialog mutation={reopenMutation} />
    </>
  )
}
